
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { SearchResult } from './types';
import { calculateRelevance, detectQueryType } from './utils';
import { mapDbClientToClient } from '@/shared/mappers/clientMappers';
import type { DbClient } from '@/types/entities/client';

export const searchClients = async (query: string, limit: number): Promise<SearchResult[]> => {
  const queryType = detectQueryType(query);
  
  // Умная фильтрация на основе типа запроса
  const selectQuery = supabaseWorker
    .from('clients')
    .select('id, full_name, phone, email, type, status, created_at');
  
  // Обертка: FetchQueryBuilder возвращает thenable, но не Promise. Превратим в настоящий Promise
  type Thenable<T> = { then: (onfulfilled: (value: T) => void, onrejected?: (reason: unknown) => void) => unknown };
  const wrapThenable = <T>(qb: Thenable<T>): Promise<T> => new Promise((resolve, reject) => qb.then(resolve, reject));

  // Worker-клиент не поддерживает .or(); выполняем параллельные запросы и объединяем результаты
  type QueryResult = { data: unknown | null; error: unknown | null };
  const promises: Array<Promise<QueryResult>> = [];
  if (queryType === 'id') {
    promises.push(wrapThenable<QueryResult>(selectQuery.eq('id', query)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('id', `${query}%`)));
  } else if (queryType === 'email') {
    promises.push(wrapThenable<QueryResult>(selectQuery.ilike('email', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('full_name', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('phone', `%${query}%`)));
  } else if (queryType === 'phone') {
    promises.push(wrapThenable<QueryResult>(selectQuery.ilike('phone', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('full_name', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('email', `%${query}%`)));
  } else {
    promises.push(wrapThenable<QueryResult>(selectQuery.ilike('full_name', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('email', `%${query}%`)));
    promises.push(wrapThenable<QueryResult>(supabaseWorker.from('clients').select('id, full_name, phone, email, type, status, created_at').ilike('phone', `%${query}%`)));
  }

  const settled = await Promise.allSettled(promises);
  let hadRejection = false;
  const rows = settled.flatMap(s => {
    if (s.status === 'fulfilled') {
      const r = s.value as QueryResult;
      return Array.isArray(r?.data) ? (r.data as unknown[]) : [];
    } else {
      hadRejection = true;
      // eslint-disable-next-line no-console
      console.error('[GlobalSearch][clients] sub-query rejected:', s.reason);
      return [];
    }
  });
  if (rows.length === 0 && hadRejection) {
    throw new Error('Clients search source failed');
  }
  const dedup = new Map<string, unknown>();
  rows.forEach((r) => {
    if (r && typeof r === 'object' && 'id' in r) {
      const id = (r as { id?: string }).id;
      if (id && !dedup.has(id)) dedup.set(id, r);
    }
  });
  const data = Array.from(dedup.values()).slice(0, limit);


  // Преобразуем DB типы в Frontend типы согласно STYLEGUIDE.md
  const clients = (data || []).map(dbClient => mapDbClientToClient(dbClient as DbClient));

  return clients.map(client => ({
    id: client.id,
    type: 'client' as const,
    title: client.fullName || 'Без имени',
    subtitle: client.phone || client.email || '',
    description: `${client.type} клиент`,
    icon: 'Users',
    url: `/clients`,
    relevance: calculateRelevance(query, [client.fullName, client.phone, client.email], queryType),
    createdAt: client.createdAt ? client.createdAt.toISOString() : new Date().toISOString(),
    status: client.status
  }));
};
